import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, NumberInput, Typography } from '@la/ds-ui-components';
import { Activity } from '@la/types';
import { ActivityTeam } from '@la/types/models';
import { getLAHostnameParts } from '@la/utilities';
import { useUpdateGameScoreMutation } from 'redux/services/singleActivityApi';
import { useGetTeamsInternalQuery } from 'redux/services/teamApi';
import { ProgramPermissions } from 'redux/services/types/singleActivity';
import { useAppSelector as useAppSelectorForUserId } from 'redux/store';
import { getUserId } from 'redux/userSlice';
import styles from './GameScore.module.scss';

export type GameScoreTeam = {
  activityId: number;
  homeAway: string;
  programId: number;
  result: string;
  score: number;
  teamId: number;
  teamName: string;
};

type GameScoreProps = {
  activity?: Activity;
  permissions?: ProgramPermissions;
};

const TBD_TEAM_ID = -2; // Id used when a team is TBD

export type GameScoreFormData = {
  homeTeamScore: number;
  awayTeamScore: number;
  gameStatus: 'played_regular_time' | 'played_overtime';
  isFormCleared: boolean;
};

const GameScore = ({ activity, permissions }: GameScoreProps) => {
  const { subdomain } = getLAHostnameParts();
  const userId = useAppSelectorForUserId(getUserId);

  const [editing, setEditing] = useState(false);
  const [homeScore, setHomeScore] = useState<number>(0);
  const [awayScore, setAwayScore] = useState<number>(0);

  const [updateGameScore, { isLoading: isUpdateLoading }] =
    useUpdateGameScoreMutation();

  const { data: teams, isLoading: isTeamsLoading } = useGetTeamsInternalQuery({
    siteDomain: subdomain,
    teamIds: activity?.teams?.map((t) => t.teamId),
  });

  const isTBDTeamPresent: boolean = useMemo(() => {
    return !!activity?.teams.find((team: ActivityTeam) => {
      return team.teamId === TBD_TEAM_ID;
    });
  }, [activity]);

  const canEditScores: boolean = useMemo(() => {
    return !!permissions?.editScores;
  }, [permissions]);

  const [homeTeam, awayTeam]: GameScoreTeam[] = useMemo(() => {
    if (!activity || !teams) {
      return [];
    }

    const sorted = activity.teams
      .map((team: ActivityTeam) => {
        const teamInfo = teams.find((teamInfo) => teamInfo?.id === team.teamId);

        return {
          activityId: team?.activityId,
          homeAway: `${team?.homeAway}`,
          programId: team?.programId,
          result: team?.result as string,
          score: team?.score ?? 0,
          teamId: team?.teamId,
          teamName:
            !teamInfo?.name || teamInfo.name === 'undefined'
              ? 'TBD'
              : teamInfo.name,
        };
      })
      .sort((a: GameScoreTeam, b: GameScoreTeam) =>
        b.homeAway.localeCompare(a.homeAway)
      );

    // Home team is always the first one in the sorted array
    setHomeScore(sorted[0].score);
    setAwayScore(sorted[1].score);
    return sorted;
  }, [teams, activity]);

  const defaultFormValues: GameScoreFormData = {
    homeTeamScore: homeTeam?.score || 0,
    awayTeamScore: awayTeam?.score || 0,
    gameStatus: 'played_regular_time',
    isFormCleared: false,
  };

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<GameScoreFormData>({
    defaultValues: { ...defaultFormValues },
    mode: 'onSubmit',
  });

  const onSubmit = (formData: GameScoreFormData) => {
    if (isUpdateLoading || isSubmitting) {
      return;
    }

    const { gameStatus } = formData;

    updateGameScore({
      subdomain,
      activityId: activity?.id.toString() ?? '',
      op: 'score',
      // This parse is needed because the number input always returns a string
      score: {
        home: parseInt(`${homeScore}`),
        away: parseInt(`${awayScore}`),
        played: gameStatus,
      },
      userId,
    })
      .unwrap()
      .catch((error: any) => {
        console.error('Failed to update game score: ', error);
      })
      .finally(() => {
        setEditing(false);
        reset();
      });
  };

  const toggleEditing = () => {
    setEditing((prev) => !prev);
  };

  if (isTeamsLoading || !activity) {
    return <p>Loading...</p>;
  }

  return (
    <div className={styles.GameScoreContainer}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }}
      >
        <div>
          {!activity?.teams?.length ? (
            <Typography variant="ui" size="xl">
              No teams available
            </Typography>
          ) : (
            <>
              <div className={styles.GameScoreTeam} key={homeTeam?.teamId || 0}>
                <Typography variant="ui" size="xl">
                  {homeTeam?.teamName || 'TBD'}
                </Typography>
                {editing ? (
                  <NumberInput
                    allowTyping
                    errorMessage={errors.homeTeamScore?.message}
                    id="homeTeamScore"
                    orientation="horizontal"
                    required
                    size="medium"
                    step={1}
                    value={homeScore}
                    max={999}
                    min={0}
                    onChangeValue={setHomeScore}
                  />
                ) : (
                  <Typography
                    className={styles.GameScoreStatic}
                    variant="ui"
                    size="xl"
                  >
                    {homeScore}
                  </Typography>
                )}
              </div>
              <div className={styles.GameScoreTeam} key={awayTeam?.teamId || 1}>
                <Typography variant="ui" size="xl">
                  {awayTeam?.teamName || 'TBD'}
                </Typography>
                {editing ? (
                  <NumberInput
                    allowTyping
                    errorMessage={errors.awayTeamScore?.message}
                    id="awayTeamScore"
                    orientation="horizontal"
                    required
                    size="medium"
                    step={1}
                    value={awayScore}
                    max={999}
                    min={0}
                    onChangeValue={setAwayScore}
                  />
                ) : (
                  <Typography
                    className={styles.GameScoreStatic}
                    variant="ui"
                    size="xl"
                  >
                    {awayScore}
                  </Typography>
                )}
              </div>
            </>
          )}
        </div>
        {/* Prevents the editing controls from being displayed if a team is TBD or if the user does not have the correct permissions to edit the game score. */}
        {!isTBDTeamPresent && canEditScores && (
          <div>
            {editing ? (
              <div className={styles.GameScoreActions}>
                <Button size="medium" type="submit" variant="primary">
                  Save
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    toggleEditing();
                    // Reset the form to its default values
                    setHomeScore(homeTeam?.score);
                    setAwayScore(awayTeam?.score);
                    reset(defaultFormValues);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="primary" size="medium" onClick={toggleEditing}>
                Edit scores
              </Button>
            )}
          </div>
        )}
        <input
          {...register('gameStatus')}
          type="hidden"
          value="played_regular_time"
        />
      </form>
    </div>
  );
};

export default GameScore;
